import { router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"
import { candidatesPath } from "@/routes"

/**
 * OKS-465 — Active⇄Removed view switch for the candidate list.
 *
 * Removal is ORTHOGONAL to pipeline status, so this is a view switch
 * (`?removed=1`) rather than an 8th status tab (UX Spec §2 / Open Q1).
 * Switching views navigates to a clean list URL (filters reset) — the
 * active and removed views are independent browsing contexts.
 *
 * Rendered near the status tabs. The Removed tab shows the live count
 * `(N)` so admins can see at a glance whether anything is recoverable.
 */
export interface CandidateActiveRemovedToggleProps {
  /** True when the Removed view is active (`?removed=1`). */
  removedView: boolean
  /** Count of removed candidates in the brand (Removed tab badge). */
  removedCount: number
}

export function CandidateActiveRemovedToggle({
  removedView,
  removedCount,
}: CandidateActiveRemovedToggleProps) {
  const { t } = useTranslation()

  return (
    <div
      role="tablist"
      aria-label={t("candidates.list.removed_view.toggle_aria")}
      data-testid="candidate-list-active-removed-toggle"
      className="bg-muted inline-flex items-center gap-1 rounded-md p-1"
    >
      <ToggleButton
        active={!removedView}
        testId="candidate-list-view-active"
        label={t("candidates.list.removed_view.tab_active")}
        onClick={() => {
          if (removedView) router.get(candidatesPath())
        }}
      />
      <ToggleButton
        active={removedView}
        testId="candidate-list-view-removed"
        label={t("candidates.list.removed_view.tab_removed", {
          count: removedCount,
        })}
        onClick={() => {
          if (!removedView) router.get(candidatesPath({ removed: 1 }))
        }}
      />
    </div>
  )
}

function ToggleButton({
  active,
  label,
  testId,
  onClick,
}: {
  active: boolean
  label: string
  testId: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "rounded px-3 py-1.5 text-sm font-medium transition-colors",
        active
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {label}
    </button>
  )
}
